import { useLazyQuery, useQuery } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { Autocomplete, useDebouncedQuery } from 'components/v2/Autocomplete'
import { IInputProps } from 'components/Input'
import { GTDriverSuggestionsResponse, GTTVehicle } from 'gql/types.generated'
import { normalize } from 'utils/string'
import { GET_DRIVERS_SUGGESTIONS } from 'features/Drivers/queries-legacy'
import { GET_VEHICLE_V2 } from 'features/Vehicles/queries-legacy'

const messages = defineMessages({
  error: {
    id: 'drivers.autocomplete.error',
    defaultMessage: 'There was an issue while loading your drivers'
  },
  empty: {
    id: 'drivers.autocomplete.empty',
    defaultMessage: 'No drivers found'
  }
})

interface IDriverAutocompleteProps extends Omit<IInputProps<HTMLInputElement>, 'onChange'> {
  onChange: (driver: IDriverAutocomplete) => void
  vehicleId?: string
  isActive?: boolean
  isShowingWithoutDriverOption?: boolean
}

interface IDriverAutocomplete {
  id: string
  name: string
}

export const WITHOUT_DRIVER: IDriverAutocomplete = {
  id: 'without-driver',
  name: 'Without driver'
}

export const DriverAutocomplete = ({
  onChange,
  vehicleId,
  isActive,
  isShowingWithoutDriverOption,
  ...props
}: IDriverAutocompleteProps): ReactElement => {
  const intl = useIntl()
  const [pattern, setPattern] = useState('')
  const [getDrivers, { data: autocompleteData, loading: autocomplateLoading, error: autocompleteError }] =
    useLazyQuery<{
      driverSuggestions: GTDriverSuggestionsResponse
    }>(GET_DRIVERS_SUGGESTIONS)
  const {
    data: vehicleData,
    loading: vehicleLoading,
    error: vehicleError
  } = useQuery<{
    tvehicle: GTTVehicle
  }>(GET_VEHICLE_V2, {
    fetchPolicy: 'network-only',
    variables: {
      input: {
        id: vehicleId
      }
    },
    skip: !vehicleId
  })
  const { isDebouncing, shouldShowItems } = useDebouncedQuery({
    query: getDrivers,
    pattern,
    extraInputs: {
      isActive
    },
    skip: !!vehicleId
  })

  const drivers: IDriverAutocomplete[] =
    (vehicleId
      ? vehicleData?.tvehicle?.drivers.map((driver) => {
          return {
            id: driver.id,
            name: driver.name || ''
          }
        })
      : autocompleteData?.driverSuggestions?.items) || []
  let shownItems = vehicleId
    ? drivers.filter((driver) => {
        return normalize(driver.name || '').indexOf(normalize(pattern)) > -1
      })
    : drivers

  if (isShowingWithoutDriverOption) {
    shownItems = [WITHOUT_DRIVER, ...shownItems]
  }

  const handleChange = (driver: IDriverAutocomplete, { inputValue = '' } = {}): void => {
    setPattern(inputValue)

    if (!isEmpty(driver)) {
      onChange(driver)
    } else {
      onChange({
        id: '',
        name: ''
      })
    }
  }

  const loading = autocomplateLoading || vehicleLoading || isDebouncing
  const error = autocompleteError || vehicleError

  return (
    <Autocomplete
      {...props}
      items={shownItems}
      getItemLabel={(driver: IDriverAutocomplete): string => driver.name}
      loading={loading}
      error={error}
      errorMessage={intl.formatMessage(messages.error)}
      emptyMessage={intl.formatMessage(messages.empty)}
      onChange={handleChange}
      shouldShowItems={shouldShowItems}
    />
  )
}
